import { useLazyQuery } from '@apollo/client'
import DropdownBase, { Option } from 'components/partials/dropDown'
import { TabProps } from 'components/partials/tabs'
import { IAgencyGroup, IAgencyGroupJobConfig } from 'pages/agency-group-management/interface'
import {
  AgencyGroupJobTypeEnum,
  GET_ALL_AGENCY_GROUP,
  GET_DETAIL_AGENCY_GROUP_BY_ID,
} from 'queries/admin/agencyGroup'
import React, { useEffect, useState } from 'react'
import EditTemplateCustomization from './Edit'
import ViewTemplateCustomization from './View'
import { STATUS } from 'constants/messages'
import { notify } from 'components/toastify'
import IconInformation from 'assets/images/icon-information.svg'
import { useSession } from 'helpers/checkSession'

interface Props extends TabProps {
  dataInput: {
    currentAgencyGroup: IAgencyGroup
  }
}

interface IDropdownAgencyGroupOption extends Option {}

const TemplateCustomization: React.FC<Props> = ({ dataInput }) => {
  const { handleErrorSession } = useSession()
  const [isEditMode, setIsEditMode] = useState(false)
  const [agencyGroupJobConfigs, setAgencyGroupJobConfigs] = useState<IAgencyGroupJobConfig[]>([])
  const [currentAgencyGroup, setCurrentAgencyGroup] = useState(dataInput.currentAgencyGroup)
  const [dropdownAgencyGroupOptions, setDropdownAgencyGroupOptions] = useState<
    IDropdownAgencyGroupOption[]
  >([])

  const [getAllAgencyGroupQuery] = useLazyQuery(GET_ALL_AGENCY_GROUP, {
    variables: {
      skip: 0,
      take: 50,
    },
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      if (!data) return

      const options: IDropdownAgencyGroupOption[] = []

      for (const agencyGroup of data.getAllAgencyGroup) {
        options.push({ id: agencyGroup.agencyGroupId, name: agencyGroup.agencyGroupName })
      }

      setDropdownAgencyGroupOptions(options)
    },
    onError: (error) => {
      if (error.message) {
        handleErrorSession(error, error.message)
      } else {
        notify('Something wrong', STATUS.ERROR)
      }
    },
  })

  const [getDetailAgencyGroupByIdQuery] = useLazyQuery(GET_DETAIL_AGENCY_GROUP_BY_ID, {
    variables: {
      customJobType: AgencyGroupJobTypeEnum.CUSTOM_EMAIL,
      agencyGroupId: currentAgencyGroup?.agencyGroupId,
    },
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      if (!data) return
      setAgencyGroupJobConfigs(data.getDetailAgencyGroupById)
    },
    onError: (error) => {
      if (error.message) {
        handleErrorSession(error, error.message)
      } else {
        notify('Something wrong', STATUS.ERROR)
      }
    },
  })

  const refetchDetailData = () => {
    getDetailAgencyGroupByIdQuery()
  }

  const handleChangeEditMode = (isEdit: boolean) => {
    setIsEditMode(isEdit)
  }

  const onSelectAgencyGroupOption = (option: IDropdownAgencyGroupOption) => {
    if (currentAgencyGroup?.agencyGroupId === option.id) return

    if (typeof option.id !== 'number') return

    const agencyGroup: IAgencyGroup = {
      agencyGroupId: option.id,
      agencyGroupName: option.name,
      agencies: [],
      agencyGroupJobConfigs: [],
    }

    setCurrentAgencyGroup(agencyGroup)
    handleChangeEditMode(false)
  }

  useEffect(() => {
    getAllAgencyGroupQuery()
  }, [])

  useEffect(() => {
    if (currentAgencyGroup?.agencyGroupId) {
      getDetailAgencyGroupByIdQuery({
        variables: {
          agencyGroupId: currentAgencyGroup.agencyGroupId,
          customJobType: AgencyGroupJobTypeEnum.CUSTOM_EMAIL,
        },
      })
    }
  }, [currentAgencyGroup?.agencyGroupId])

  return (
    <div className="px-20 md:px-10 space-y-2">
      <div className="pb-5 flex">
        <img className="h-5" src={IconInformation} alt="Icon-Information" />
        <span className="italic text-sm">
          Management the customization email template by agency group
        </span>
      </div>
      <div className="w-full">
        <span>
          <strong>Agency Group</strong>
        </span>
        <div className="flex w-full space-x-2">
          <DropdownBase
            className="w-full"
            options={dropdownAgencyGroupOptions}
            initialValue={currentAgencyGroup?.agencyGroupName ?? ''}
            handleOptions={(option) =>
              onSelectAgencyGroupOption(option as IDropdownAgencyGroupOption)
            }
            placeHolder="Select agency group to add or edit"
          />
        </div>
      </div>
      {isEditMode ? (
        <EditTemplateCustomization
          agencyGroup={currentAgencyGroup}
          handleChangeEditMode={handleChangeEditMode}
          agencyGroupJobConfigs={agencyGroupJobConfigs}
          refetchDetailData={refetchDetailData}
        />
      ) : (
        <ViewTemplateCustomization
          agencyGroup={currentAgencyGroup}
          handleChangeEditMode={handleChangeEditMode}
          agencyGroupJobConfigs={agencyGroupJobConfigs}
          refetchDetailData={refetchDetailData}
        />
      )}
    </div>
  )
}

export default TemplateCustomization
